import React from 'react';
import PropTypes from 'prop-types';

import {
	makeStyles,
	IconButton
} from '@material-ui/core';

// State colors (per design).
const BORDER_DEFAULT = '#C0C4C6';
const DOTS_DEFAULT = '#697277';
const DOTS_HOVER = '#000000';
const FOCUS_BORDER = '#99CBD2';
const FOCUS_DOTS = '#007E8F';

const styles = makeStyles(theme => ({
	button: {
		// No extra padding: the icon already includes its own rounded-tile
		// background and border, so the button footprint must equal the icon
		// size to avoid growing the header row height.
		padding: 0,
		borderRadius: theme.spacing(1.75),

		// The border rect and the dots path are recolored per interaction
		// state. Classes below target them by className on the inline SVG.
		'&:hover': {
			backgroundColor: 'transparent',
			'& $tileBorder': {
				stroke: BORDER_DEFAULT
			},
			'& $dots': {
				fill: DOTS_HOVER
			}
		},

		'&:focus-visible': {
			outline: 'none',
			'& $tileBorder': {
				stroke: FOCUS_BORDER
			},
			'& $dots': {
				fill: FOCUS_DOTS
			}
		}
	},
	
	icon: {
		width: theme.spacing(5), // 28px — fits within the 34px toolbar content height
		height: theme.spacing(5),
		display: 'block'
	},

	// Default state colors; overridden by the hover/focus rules above.
	tileBorder: {
		stroke: BORDER_DEFAULT
	},

	dots: {
		fill: DOTS_DEFAULT
	}
}));

/**
 * Bento (grid) icon button that opens the App Switcher.
 * Sits alongside the existing header icons in the DTD header.
 *
 * The Bento glyph is rendered inline (rather than as an <img>) so its border
 * and dots can be recolored on hover/focus.
 */
function AppSwitcherButton(props) {
	const classes = styles();
	const { onClick } = props;

	return (
		<IconButton
			className={classes.button}
			onClick={onClick}
			aria-label="open app switcher"
			aria-haspopup="dialog">
			<svg
				className={classes.icon}
				viewBox="0 0 40 40"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				aria-hidden="true"
				focusable="false">
				<rect x="0.625" y="0.625" width="38.75" height="38.75" rx="14.375" fill="white" />
				<rect
					className={classes.tileBorder}
					x="0.625"
					y="0.625"
					width="38.75"
					height="38.75"
					rx="14.375"
					strokeWidth="1.25" />
				<g clipPath="url(#appSwitcherBentoClip)">
					<path
						className={classes.dots}
						fillRule="evenodd"
						clipRule="evenodd"
						d="M14.375 23.75C13.3438 23.75 12.5 24.5938 12.5 25.625C12.5 26.6562 13.3438 27.5 14.375 27.5C15.4062 27.5 16.25 26.6562 16.25 25.625C16.25 24.5938 15.4062 23.75 14.375 23.75ZM14.375 18.125C13.3438 18.125 12.5 18.9688 12.5 20C12.5 21.0312 13.3438 21.875 14.375 21.875C15.4062 21.875 16.25 21.0312 16.25 20C16.25 18.9688 15.4062 18.125 14.375 18.125ZM14.375 12.5C13.3438 12.5 12.5 13.3438 12.5 14.375C12.5 15.4062 13.3438 16.25 14.375 16.25C15.4062 16.25 16.25 15.4062 16.25 14.375C16.25 13.3438 15.4062 12.5 14.375 12.5ZM25.625 16.25C26.6562 16.25 27.5 15.4062 27.5 14.375C27.5 13.3438 26.6562 12.5 25.625 12.5C24.5938 12.5 23.75 13.3438 23.75 14.375C23.75 15.4062 24.5938 16.25 25.625 16.25ZM25.625 18.125C24.5938 18.125 23.75 18.9688 23.75 20C23.75 21.0312 24.5938 21.875 25.625 21.875C26.6562 21.875 27.5 21.0312 27.5 20C27.5 18.9688 26.6562 18.125 25.625 18.125ZM20 12.5C18.9688 12.5 18.125 13.3438 18.125 14.375C18.125 15.4062 18.9688 16.25 20 16.25C21.0312 16.25 21.875 15.4062 21.875 14.375C21.875 13.3438 21.0312 12.5 20 12.5ZM25.625 23.75C24.5938 23.75 23.75 24.5938 23.75 25.625C23.75 26.6562 24.5938 27.5 25.625 27.5C26.6562 27.5 27.5 26.6562 27.5 25.625C27.5 24.5938 26.6562 23.75 25.625 23.75ZM20 18.125C18.9688 18.125 18.125 18.9688 18.125 20C18.125 21.0312 18.9688 21.875 20 21.875C21.0312 21.875 21.875 21.0312 21.875 20C21.875 18.9688 21.0312 18.125 20 18.125ZM20 23.75C18.9688 23.75 18.125 24.5938 18.125 25.625C18.125 26.6562 18.9688 27.5 20 27.5C21.0312 27.5 21.875 26.6562 21.875 25.625C21.875 24.5938 21.0312 23.75 20 23.75Z" />
				</g>
				<defs>
					<clipPath id="appSwitcherBentoClip">
						<rect width="15" height="15" fill="white" transform="translate(12.5 12.5)" />
					</clipPath>
				</defs>
			</svg>
		</IconButton>
	);
}

AppSwitcherButton.propTypes = {
	onClick: PropTypes.func.isRequired
};

export default AppSwitcherButton;
